import { IMG } from "./images";

// Every booking CTA on the site points here.
export const GYG_URL = "https://www.getyourguide.com/";

export const BRAND = {
  name: "Roman Colosseum Tickets",
  shortName: "Colosseum Tickets",
  domain: "romancolosseumtickets.com",
  tagline: "Your independent guide to visiting the Colosseum.",
};

// Primary navigation grouped logically.
export const NAV = [
  {
    label: "Tickets",
    to: "/tickets",
    columns: [
      {
        heading: "Popular tickets",
        links: [
          { label: "Entry Tickets", to: "/tickets/entry" },
          { label: "Skip-the-Line", to: "/tickets/skip-the-line" },
          { label: "Guided Tours", to: "/tickets/guided-tours" },
          { label: "Underground Tours", to: "/tickets/underground" },
        ],
      },
      {
        heading: "Special experiences",
        links: [
          { label: "Arena Floor", to: "/tickets/arena-floor" },
          { label: "Night Tours", to: "/tickets/night-tours" },
          { label: "Combo Tickets", to: "/tickets/combo" },
          { label: "Family Tickets", to: "/tickets/family" },
          { label: "Last Minute", to: "/tickets/last-minute" },
        ],
      },
    ],
  },
  {
    label: "Plan Your Visit",
    to: "/plan",
    columns: [
      {
        heading: "Before you go",
        links: [
          { label: "Opening Hours", to: "/plan/opening-hours" },
          { label: "Best Time to Visit", to: "/plan/best-time-to-visit" },
          { label: "How to Reach", to: "/plan/how-to-reach" },
          { label: "Entrances", to: "/plan/entrances" },
          { label: "Interactive Map", to: "/plan/map" },
        ],
      },
      {
        heading: "On the day",
        links: [
          { label: "Rules & Regulations", to: "/plan/rules" },
          { label: "Tips for Visiting", to: "/plan/tips" },
          { label: "Parking", to: "/plan/parking" },
          { label: "Restaurants Nearby", to: "/plan/restaurants" },
          { label: "Nearby Attractions", to: "/plan/nearby-attractions" },
        ],
      },
    ],
  },
  {
    label: "Highlights",
    to: "/highlights",
    columns: [
      {
        heading: "Inside the monument",
        links: [
          { label: "Underground (Hypogeum)", to: "/highlights/underground" },
          { label: "Arena Floor", to: "/highlights/arena-floor" },
          { label: "Colosseum Architecture", to: "/highlights/architecture" },
        ],
      },
      {
        heading: "The wider site",
        links: [
          { label: "Roman Forum", to: "/highlights/roman-forum" },
          { label: "Palatine Hill", to: "/highlights/palatine-hill" },
        ],
      },
    ],
  },
  {
    label: "Visitor Guides",
    to: "/guides",
    columns: [
      {
        heading: "Editorial guides",
        links: [
          { label: "History of the Colosseum", to: "/guides/history" },
          { label: "Gladiators at the Colosseum", to: "/guides/gladiators" },
          { label: "Visiting for the First Time", to: "/guides/first-time" },
        ],
      },
      {
        heading: "Trip planning",
        links: [
          { label: "Visiting with Kids", to: "/guides/with-kids" },
          { label: "Colosseum & Vatican in One Day", to: "/guides/colosseum-vatican-one-day" },
        ],
      },
    ],
  },
];

export const STATS = [
  { value: "7M+", label: "Visitors every year" },
  { value: "1,950", label: "Years since opening" },
  { value: "50,000", label: "Spectators it once held" },
  { value: "4.8/5", label: "Average traveller rating" },
];

export const TRUST_POINTS = [
  {
    icon: "ShieldCheck",
    title: "Verified partners only",
    text: "We compare official and trusted resellers so you book with confidence — never a grey-market seller.",
  },
  {
    icon: "BadgePercent",
    title: "Transparent pricing",
    text: "Clear, side-by-side prices with no hidden fees. See exactly what each ticket includes before you commit.",
  },
  {
    icon: "CalendarCheck",
    title: "Free cancellation options",
    text: "Most tickets we list can be cancelled up to 24 hours before your visit for a full refund.",
  },
  {
    icon: "Smartphone",
    title: "Instant mobile tickets",
    text: "Skip the print queue. Your ticket arrives by email and lives on your phone, ready to scan at the gate.",
  },
];

export const REVIEWS = [
  {
    name: "Sofia M.",
    country: "Spain",
    rating: 5,
    title: "Skipped a 90-minute queue",
    text: "We compared options here and chose the guided underground tour. Walking onto the arena floor where gladiators fought was unforgettable. Our guide brought it all to life.",
    ticket: "Underground Tour",
  },
  {
    name: "James R.",
    country: "United Kingdom",
    rating: 5,
    title: "So easy to compare",
    text: "I had no idea there were so many ticket types. The comparison made it obvious which one suited our family. Booked in two minutes and the QR code worked perfectly.",
    ticket: "Family Ticket",
  },
  {
    name: "Aiko T.",
    country: "Japan",
    rating: 5,
    title: "Night tour was magical",
    text: "Visiting after dark with almost no crowds felt like having ancient Rome to ourselves. The lighting on the arches was breathtaking. Highly recommend booking ahead.",
    ticket: "Night Tour",
  },
  {
    name: "Daniel & Mara",
    country: "Germany",
    rating: 5,
    title: "Perfect first visit",
    text: "As first-timers we were nervous about logistics. The planning guides answered every question — entrances, security, best time to arrive. Everything went smoothly.",
    ticket: "Skip-the-Line",
  },
];

export const TOP_FAQS = [
  {
    q: "Do I really need to book Colosseum tickets in advance?",
    a: "Yes. The Colosseum operates a timed-entry system and daily capacity is capped. On-site tickets frequently sell out, and the standby queue can exceed 90 minutes in summer. Booking ahead guarantees your time slot and lets you walk straight to the security check.",
  },
  {
    q: "What is the difference between standard entry and a guided tour?",
    a: "A standard entry ticket gives you self-guided access to the Colosseum's first and second tiers, plus the Roman Forum and Palatine Hill. A guided tour adds an expert who explains the history and often unlocks restricted areas such as the arena floor or the underground.",
  },
  {
    q: "Can I visit the underground and arena floor with a normal ticket?",
    a: "No. The underground (Hypogeum) and arena floor are restricted zones accessible only on a special ticket or guided tour. These sell out fastest, so book well ahead if they are on your list.",
  },
  {
    q: "Is the Roman Forum and Palatine Hill included?",
    a: "Most Colosseum tickets include a single, combined entry to the Roman Forum and Palatine Hill, valid for the same day or within 24 hours. Always check the specific ticket's inclusions before booking.",
  },
  {
    q: "Are tickets refundable if my plans change?",
    a: "Many tickets offered through our partners include free cancellation up to 24 hours before your selected time. Look for the 'free cancellation' label on each option before you book.",
  },
];
